/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import React, { FC, Fragment, useCallback, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { compact, isEmpty } from "lodash"

import { useTranslationsMap } from "common/utils/i18n"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { GrabXDataTypes, MerchantPortalFeatureFlag } from "grabx"
import { generateDefaultFacets } from "grabx/facets"
import { useGrabXFeatureFlag } from "queries"
import { ExtraReviewsFilterConfig, Review } from "types/api/menuFeedback.type"
import { ActionType } from "types/models/feedback"
import InfiniteScroll from "components/InfiniteScroll"
import { TroyWebTracker } from "utils/tracking"
import {
  EventAction,
  EventCategory,
  EventCategoryEng,
  EventActionEng,
} from "constants/tracking"
import { useCanViewStoreList } from "order-manager/hooks/useCanViewStoreList"
import useSingleStoreUser from "hooks/useSingleStoreUser"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import {
  useFetchMerchantDetailQuery,
  useSearchOutletsPaginatedInfiniteQuery,
  flattenInfiniteOutlets,
} from "queries/menuApi"
import { BusinessType } from "types/models/menu"
import { FEEDBACK_FILTER_LIMIT } from "components/search/searchBarConstants"

import { FeatureFlag } from "constants/features"
import { storageHelper } from "utils/storage"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { Tabs } from "@duxton-web/ui"
import { Redirect, useHistory, useParams } from "react-router"
import { RatingSummary } from "./FeedbackRatings/RatingSummary"
import { FeedbackFilters } from "./FeedbackFilters"
import FeedbackList from "./FeedbackList"
import { FeedbackDrawerContainer } from "./FeedbackDrawerContainer"
import {
  DEFAULT_PAGE_SIZE,
  FeedbackTabKeys,
  RETURN_REFUND_TAB_FRAGMENT,
  TAB_ROUTES,
  VALID_TAB_FRAGMENTS,
} from "./consts"
import FeedbackDownload from "./FeedbackDownload"
import FeedbackTopFiltersNew from "./FeedbackFilters/FeedbackTopFiltersNew"
import { IncidentList } from "./Incidents"
import { hasReturnRefundStore, ReturnRefundList } from "./ReturnRefund"

export const Feedback: React.FC = () => {
  const { feedback, menu } = useSelector((state: AppState) => state)
  const { outlets } = menu
  const { loading, overview, overviews, filterState, reviews } = feedback
  const dispatch = useDispatch()

  const {
    flags: { fetchMerchantDetail: isFetchMerchantDetailRtkEnabled },
    isLoading: isLoadingRtkQueryMigrationEndpointsFlag,
  } = useRtkQueryMigrationEndpointsFlag()

  const textMap = useTranslationsMap({
    customerRatings: "feedback.headings.customerRatings",
    customerReviews: "feedback.headings.customerReviews",
  })

  const onClickRow = (actionType: ActionType, review: Review) => {
    dispatch(
      actionCreator.feedbackDrawer.toggleReply({
        visible: true,
        actionType,
        review,
      }),
    )
  }

  const canViewStoreList = useCanViewStoreList()

  const { isSingleStoreUser, storeID } = useSingleStoreUser()

  // available stores
  let merchantIDs = outlets?.map(o => o.merchantID) ?? []
  // use fallback value from local storage
  if (isEmpty(merchantIDs) || !canViewStoreList)
    merchantIDs = compact([storeID])

  const singleMerchantID = isSingleStoreUser ? merchantIDs[0] : undefined

  const { data: merchantDetailRtk } = useFetchMerchantDetailQuery(
    {
      merchantID: singleMerchantID ?? "",
      countryCode: storageHelper.getUserCountry() || "",
    },
    { skip: !singleMerchantID || !isFetchMerchantDetailRtkEnabled },
  )

  const outletV2 = isFetchMerchantDetailRtkEnabled
    ? merchantDetailRtk?.merchant ?? null
    : menu.outletV2

  const handleLoadMore = () => {
    if (!isEmpty(merchantIDs)) {
      dispatch(
        actionCreator.feedback.fetchMoreReviews({
          body: {
            ...filterState,
          },
        }),
      )
    }
  }

  useEffect(() => {
    if (
      singleMerchantID &&
      !isLoadingRtkQueryMigrationEndpointsFlag &&
      !isFetchMerchantDetailRtkEnabled
    ) {
      // in single store mode, outlets is empty so we need to fetch the store detail here
      dispatch(
        actionCreator.menu.fetchMerchantDetail({
          merchantID: singleMerchantID,
          countryCode: storageHelper.getUserCountry() || "",
        }),
      )
      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.fetchMerchantDetail,
        labels: {
          MERCHANT_ID: singleMerchantID,
          IS_RTK_ENABLED: isFetchMerchantDetailRtkEnabled,
          FROM_RTK_QUERY: false,
          FROM_OLD_REDUX: true,
          ACTION_NAME: "fetchMerchantDetail",
        },
      })
    }
  }, [
    dispatch,
    singleMerchantID,
    isLoadingRtkQueryMigrationEndpointsFlag,
    isFetchMerchantDetailRtkEnabled,
  ])

  // RTK Query path tracking — fires when RTK flag is on and merchantID is available.
  // Note: singleMerchantID derives from outlets (re-fetchable), so this effect can
  // re-fire if singleMerchantID toggles. Do not add deps carelessly to avoid
  // duplicate tracking events.
  useEffect(() => {
    if (
      singleMerchantID &&
      !isLoadingRtkQueryMigrationEndpointsFlag &&
      isFetchMerchantDetailRtkEnabled
    ) {
      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.fetchMerchantDetail,
        labels: {
          MERCHANT_ID: singleMerchantID,
          IS_RTK_ENABLED: isFetchMerchantDetailRtkEnabled,
          FROM_RTK_QUERY: true,
          FROM_OLD_REDUX: false,
          ACTION_NAME: "fetchMerchantDetail",
        },
      })
    }
  }, [
    singleMerchantID,
    isLoadingRtkQueryMigrationEndpointsFlag,
    isFetchMerchantDetailRtkEnabled,
  ])

  const supportsDineIn =
    outletV2?.supportDineIn ||
    outlets?.some(info => {
      return info.deliverOption?.includes("DINEIN")
    })

  const hasSelfPickup =
    outletV2?.supportTakeaway ||
    outlets?.some(info => info.deliverOption?.includes("TAKEAWAY"))

  const enableNonTxnReview =
    storageHelper.isFeatureEnabled(FeatureFlag.enableNonTxnReviewMexPortal) &&
    (storageHelper.isFeatureEnabled(
      FeatureFlag.enableNonTxnReviewMexPortalForDeliveryOnly,
    ) ||
      supportsDineIn)

  useEffect(() => {
    TroyWebTracker.sendEvent({
      category: EventCategory.feedbackHomePage,
      action: EventAction.pageView,
      labels: {
        DEPRECATE_REVIEW_ORDER_ID: true,
      },
    })
    // only want to handle this once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFetchReviews = (
    extras?: ExtraReviewsFilterConfig & { merchantIDs?: string[] },
  ) => {
    if (!isEmpty(merchantIDs)) {
      const body = {
        ...filterState,
        ...extras,
        nextToken: undefined, // ensures a new list sequence
      }

      // to bypass filters
      if (isSingleStoreUser) body.merchantIDs = merchantIDs

      dispatch(
        actionCreator.feedback.fetchReviews({
          body,
        }),
      )
    }
  }

  useEffect(() => {
    if (
      (reviews?.length ?? 0) <= DEFAULT_PAGE_SIZE &&
      filterState?.nextToken &&
      !loading
    ) {
      handleLoadMore()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviews, filterState, loading])

  return (
    <Fragment>
      <div
        css={css({
          marginBottom: "23px",
        })}
      >
        <FeedbackTopFiltersNew
          enableNonTxnReview={enableNonTxnReview}
          onFetchReviews={handleFetchReviews}
          isSingleStore={isSingleStoreUser}
        />
      </div>

      <div
        css={css({
          marginBottom: "25px",
        })}
      >
        <span
          css={css({
            fontWeight: 500,
            fontSize: "16px",
          })}
        >
          {textMap.customerRatings}
        </span>
        <div
          css={css({
            marginTop: "26px",
            display: "flex",
            gap: "16px",
          })}
        >
          {overview && (
            <React.Fragment>
              <RatingSummary
                feedbackOverview={overview}
                hasSelfPickup={hasSelfPickup}
              />
            </React.Fragment>
          )}
          {overviews &&
            Object.entries(overviews).map(([serviceType, feedbackOverview]) => (
              <React.Fragment key={serviceType}>
                <RatingSummary
                  key={serviceType}
                  feedbackOverview={feedbackOverview}
                  serviceType={serviceType}
                  hasSelfPickup={hasSelfPickup}
                />
              </React.Fragment>
            ))}
        </div>
      </div>

      <div
        css={css({
          marginBottom: "16px",
        })}
      >
        <span
          css={css({
            fontWeight: 600,
            fontSize: "14px",
          })}
        >
          {textMap.customerReviews}
        </span>
      </div>

      <div
        css={css({
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "14px",
        })}
      >
        <FeedbackFilters
          onFetchReviews={handleFetchReviews}
          merchantID={singleMerchantID}
        />
        <FeedbackDownload />
      </div>

      <InfiniteScroll
        onReachEnd={handleLoadMore}
        hasMore={!isEmpty(filterState?.nextToken)}
        loading={loading}
      >
        <FeedbackList
          data-testid="feedback-list-table"
          onClick={onClickRow}
          onFetchReviews={handleFetchReviews}
          enableNonTxnReview={enableNonTxnReview}
        />
      </InfiniteScroll>
      <FeedbackDrawerContainer />
    </Fragment>
  )
}

export function FeedbackTabSwitcher() {
  const textMap = useTranslationsMapTyped({
    feedbackTitle: "feedback.tabTitle",
    incidentTitle: "incidents.title",
  })
  const returnRefundText = useTranslationsMap({
    tabTitle: "ecommerce.sellerCentre.returns.tabTitle",
  })
  const params = useParams<{ fragment?: string }>()
  const history = useHistory()
  const { fragment } = params
  const { outlets, outletV2, merchantGroupID } = useSelector(
    (state: AppState) => state.menu,
  )
  const { isSingleStoreUser, storeID } = useSingleStoreUser()

  /*
   * Same gate as the Orders tab: the GrabX flag is the kill switch, and eligibility comes
   * from a store's own `businessType`. `BusinessType.Shop` covers mart *and* eCommerce, so
   * the account-level `hasEcommerceStore()` profile flag is not OR-ed in — it added no
   * merchant that `Shop` does not already cover, and handed the tab to food merchants whose
   * profile carried the flag.
   *
   * Read before the mart-store search below so the search itself can skip on it — the
   * kill switch should stop everything this feature added, not just hide the tab, and a
   * food-only merchant should never fire this search at all.
   */
  const {
    currentData: enableEcomReturnRefund = false,
    isFetching: isReturnRefundFlagLoading,
  } = useGrabXFeatureFlag({
    variableName: MerchantPortalFeatureFlag.portalEnableEcomReturnRefund,
    dataType: GrabXDataTypes.Boolean,
    defaultValue: false,
    facets: generateDefaultFacets(),
  })

  /*
   * Whether the merchant has a mart/eCommerce store, for the Return/Refund tab gate.
   * Deliberately not `state.menu.outlets.some(isMartStore)`: `outlets` is populated by
   * `NavigationContainer` with `stopAfterFirstPage: true` (100 stores, one page, never
   * more unless something else in the session happens to exhaust it first) — a merchant
   * with over 100 stores whose only mart store lands past that first page would never
   * show the tab, non-deterministically depending on what they clicked first this
   * session. This search is scoped to `BusinessType.Shop` server-side, so it finds a
   * mart store regardless of how many food stores the merchant has or their order.
   *
   * Called directly rather than through `useFeedbackStorelist`: that hook also owns the
   * merchant-aggregate fetch and its own Scribe tracking, so mounting it here purely for
   * this boolean would fire both a second time alongside the instance in
   * `FeedbackTopFiltersNew`/`IncidentList`. The args match `ReturnRefundList`'s own initial
   * filter state exactly, so this still shares its RTK Query cache entry — no extra
   * request, just no extra side effects either.
   */
  const shouldFetchMartStores = !!merchantGroupID && enableEcomReturnRefund
  const {
    data: martStoresInfiniteData,
    isLoading: isFetchingMartStoresForReturnRefundTab,
  } = useSearchOutletsPaginatedInfiniteQuery(
    {
      search: "",
      limit: FEEDBACK_FILTER_LIMIT,
      modelType: "integrated",
      businessType: [BusinessType.Shop],
    },
    { skip: !shouldFetchMartStores },
  )
  const hasMartStore = flattenInfiniteOutlets(martStoresInfiniteData).length > 0

  /*
   * A single-store merchant's business type comes from the merchant detail, and WHERE that
   * lands depends on the RTK migration flag: on the RTK path the legacy thunk is skipped and
   * `menu.outletV2` is never written, so reading Redux alone would leave this `undefined`
   * forever. `Feedback` resolves the same two sources a few lines above; this mirrors it,
   * and RTK Query dedupes the shared request.
   */
  const {
    flags: { fetchMerchantDetail: isFetchMerchantDetailRtkEnabled },
    isLoading: isMigrationFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()
  const {
    data: merchantDetailRtk,
    isFetching: isFetchingMerchantDetail,
  } = useFetchMerchantDetailQuery(
    {
      merchantID: storeID ?? "",
      countryCode: storageHelper.getUserCountry() || "",
    },
    {
      skip: !isSingleStoreUser || !storeID || !isFetchMerchantDetailRtkEnabled,
    },
  )
  const singleStoreDetail = isFetchMerchantDetailRtkEnabled
    ? merchantDetailRtk?.merchant ?? null
    : outletV2
  const handleTabChange = useCallback(
    (key: string) => {
      if (key === fragment) {
        return
      }
      history.push(TAB_ROUTES[key as FeedbackTabKeys] ?? TAB_ROUTES.feedback)
    },
    [fragment, history],
  )

  /*
   * Unlike the Orders page, an account-level check is the right granularity here: this
   * tab carries its own store picker — now scoped to `BusinessType.Shop` itself — so it is
   * asking "does this merchant have any mart/eCommerce store", not "is the selected store
   * one". Both halves therefore key off the same predicate, and the tab can no longer
   * appear over a picker with nothing eligible in it.
   */
  const showReturnRefundTab =
    hasReturnRefundStore(hasMartStore, singleStoreDetail) &&
    enableEcomReturnRefund

  const isReturnRefundFragment = fragment === RETURN_REFUND_TAB_FRAGMENT
  /*
   * `return-refund` is validated separately from `VALID_TAB_FRAGMENTS` because it is
   * gated. Treating it as unconditionally valid would leave a food merchant following a
   * shared link on a tab bar with nothing selected and an empty body, rather than
   * redirecting them. The flag and the mart-store search both resolve asynchronously, so
   * hold the redirect until they have — otherwise a merchant who does have the tab gets
   * bounced off it on first paint.
   */
  const isResolvingReturnRefundAccess =
    isReturnRefundFragment &&
    (isReturnRefundFlagLoading ||
      // `outlets` is `undefined` until NavigationContainer's own store-list fetch
      // resolves it (pre-existing signal, kept as-is here).
      outlets === undefined ||
      isFetchingMartStoresForReturnRefundTab ||
      /*
       * A single-store merchant's search above is empty by design (see `hasMartStore`'s
       * comment), with their business type arriving separately in `outletV2`/
       * `merchantDetailRtk`. Waiting only on the search would let the redirect fire while
       * that detail is still in flight, unmounting the switcher before it loads —
       * permanently locking an eligible single-store mart merchant out of the tab.
       *
       * Readiness is taken from each path's own signal rather than from the value: on the
       * RTK path `isFetching`, on the legacy path `menu.outletV2 === undefined` (it is
       * absent from the initial state, so `undefined` means "not loaded" and `null` means
       * "loaded, nothing there"). Reading the value alone would hang forever on the RTK
       * path, where Redux is never written.
       */
      (isSingleStoreUser &&
        (isMigrationFlagLoading ||
          isFetchingMerchantDetail ||
          (!isFetchMerchantDetailRtkEnabled && outletV2 === undefined))))

  if (
    fragment &&
    !isResolvingReturnRefundAccess &&
    !VALID_TAB_FRAGMENTS.includes(fragment as FeedbackTabKeys) &&
    !(isReturnRefundFragment && showReturnRefundTab)
  ) {
    // note: if we ever implement Feedback detail deeplinking, do feel free to remove the redirect
    return <Redirect to={TAB_ROUTES.feedback} />
  }

  return (
    <Fragment>
      <Tabs activeKey={fragment} onChange={handleTabChange}>
        <Tabs.TabPane tab={textMap.feedbackTitle} key="feedback">
          <Feedback />
        </Tabs.TabPane>
        <Tabs.TabPane tab={textMap.incidentTitle} key="incidents">
          <IncidentList />
        </Tabs.TabPane>
        {showReturnRefundTab && (
          <Tabs.TabPane
            tab={returnRefundText.tabTitle}
            key={RETURN_REFUND_TAB_FRAGMENT}
          >
            <ReturnRefundList />
          </Tabs.TabPane>
        )}
      </Tabs>
    </Fragment>
  )
}

export const WithFeedbackTitle = () => {
  return (WrappedComponent: React.FC) => {
    const Component: FC = props => {
      const textMap = useTranslationsMap({
        title: "feedback.title",
      })

      return (
        <div
          css={css({
            padding: "35px",
          })}
        >
          <div
            css={css({
              marginBottom: "33px",
            })}
          >
            <span
              css={css({
                fontWeight: 500,
                fontSize: "32px",
              })}
            >
              {textMap.title}
            </span>
          </div>
          <WrappedComponent {...props} />
        </div>
      )
    }
    return Component
  }
}

export const FeedbackWithHeader = WithFeedbackTitle()(FeedbackTabSwitcher)
